import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import type {
  Session,
  User
} from '@supabase/supabase-js';
import {
  supabase,
  supabaseConfigured
} from '../supabaseClient.ts';

type Role =
  | 'admin'
  | 'customer'
  | null;

type AuthValue = {
  loading: boolean;
  configured: boolean;
  session: Session | null;
  user: User | null;
  role: Role;
  signIn: (
    email: string,
    password: string
  ) => Promise<string | null>;
  signOut: () => Promise<void>;
};

const AuthContext =
  createContext<AuthValue | undefined>(
    undefined
  );

export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [session, setSession] =
    useState<Session | null>(null);

  const [role, setRole] =
    useState<Role>(null);

  const [loading, setLoading] =
    useState(true);

  async function resolveRole(
    nextSession: Session | null
  ): Promise<Role> {
    if (!supabase || !nextSession?.user) {
      return null;
    }

    const { data, error } =
      await supabase.rpc(
        'current_user_role_v1'
      );

    if (error) {
      return null;
    }

    return data === 'admin' ||
      data === 'customer'
      ? data
      : null;
  }

  async function applySession(
    nextSession: Session | null
  ) {
    setSession(nextSession);

    const nextRole =
      await resolveRole(nextSession);

    setRole(nextRole);
  }

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    let mounted = true;

    supabase.auth
      .getSession()
      .then(async ({ data }) => {
        if (!mounted) return;

        await applySession(
          data.session
        );

        if (mounted) {
          setLoading(false);
        }
      });

    const { data: sub } =
      supabase.auth.onAuthStateChange(
        (_event, newSession) => {
          if (!mounted) return;

          setSession(newSession);
          setLoading(true);

          // De callback zelf kort houden; de
          // beveiligde rol-RPC volgt direct erna.
          window.setTimeout(() => {
            if (!mounted) return;

            void (async () => {
              const nextRole =
                await resolveRole(
                  newSession
                );

              if (!mounted) return;

              setRole(nextRole);
              setLoading(false);
            })();
          }, 0);
        }
      );

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      loading,
      configured:
        supabaseConfigured,
      session,
      user:
        session?.user ?? null,
      role,

      signIn: async (
        email,
        password
      ) => {
        if (!supabase) {
          return (
            'Supabase is nog niet gekoppeld.'
          );
        }

        setLoading(true);

        const { data, error } =
          await supabase.auth
            .signInWithPassword({
              email,
              password
            });

        if (error) {
          setLoading(false);
          return error.message;
        }

        await applySession(
          data.session
        );

        setLoading(false);
        return null;
      },

      signOut: async () => {
        if (supabase) {
          await supabase.auth
            .signOut();
        }

        setSession(null);
        setRole(null);
      }
    }),
    [
      loading,
      session,
      role
    ]
  );

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context =
    useContext(AuthContext);

  if (!context) {
    throw new Error(
      'useAuth must be inside AuthProvider'
    );
  }

  return context;
}
